import { FC, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppNavigate } from '@/shared/lib';
import { MasterDetailLayout } from '@/shared/ui';
import { applyKmEntitiesFilter, type KmEntitiesFilterKey, parseKmEntitiesFilter } from '../../model/entitiesFilter';
import { KmEntitiesFilters } from './ui/KmEntitiesFilters';
import { KmEntityCard } from './ui/KmEntityCard';
import { KmEntityList } from './ui/KmEntityList';

/**
 * Страница «Сущности»: слева список сущностей модели знаний с фильтрами, справа — карточка
 * выбранной сущности. Отбор живёт в адресе страницы, поэтому ссылка открывает тот же срез.
 */
export const KmEntitiesPage: FC = () => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { domain, type, related, search } = parseKmEntitiesFilter(searchParams);

  // Отбор пишется в адрес поверх текущей записи: иначе «Назад» браузера отматывал бы раздел по одному условию
  const handleFilterChange = useCallback(
    (key: KmEntitiesFilterKey, value: string | string[]) => {
      setSearchParams(applyKmEntitiesFilter(searchParams, key, value), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Отбор переживает и выбор сущности, и возврат к списку
  const handleSelect = (entityId: number) => {
    navigate('/entities/:id', { id: String(entityId) }, { saveParams: true });
  };

  const handleBack = () => {
    navigate('/entities', undefined, { saveParams: true });
  };

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(id)}
      onBack={handleBack}
      toolbar={
        <KmEntitiesFilters
          domain={domain}
          type={type}
          related={related}
          search={search}
          onChange={handleFilterChange}
        />
      }
      list={
        <KmEntityList
          domain={domain}
          type={type}
          related={related}
          search={search}
          selectedId={id}
          onSelect={handleSelect}
        />
      }
      detail={<KmEntityCard key={id} id={id} />}
    />
  );
};
